import api from './api';
import type {
    BacktestStats,
    PatternDetectResult,
    PatternInfo,
    PatternScope,
    ZoneDetectResult,
} from '../types/patternRecognition';
import type {AIAnalysis} from '../pages/tools/pattern/types';

/** 形态类型（含 ALL 通配） */
export type PatternScopeType = PatternScope | 'ALL';

/** 获取支持的形态列表（scope: CANDLE/TREND/ALL） */
export async function getPatternList(scope?: PatternScopeType): Promise<PatternInfo[]> {
    const res = await api.get('/api/v1/patterns/list', {
        params: scope ? {scope} : undefined,
    });
    return res.data?.data ?? [];
}

/** 检测指定交易对的K线形态（scope: CANDLE/TREND/ALGORITHM） */
export async function detectPatterns(
    symbol: string,
    interval: string = '1H',
    provider: string = 'OKX',
    limit: number = 10,
    scope?: PatternScopeType,
): Promise<PatternDetectResult[]> {
    const res = await api.get(`/api/v1/patterns/detect/${symbol}`, {
        params: {interval, provider, limit, scope},
    });
    return res.data?.data ?? [];
}

/** 查询已持久化的形态结果（只读，不触发检测，供定时轮询；scope: CANDLE/TREND/ALGORITHM） */
export async function getPatternResults(
    symbol: string,
    interval: string = '1H',
    limit: number = 30,
    startTime?: number,
    endTime?: number,
    scope?: PatternScopeType,
    apiKeyId?: number,
): Promise<PatternDetectResult[]> {
    const res = await api.get(`/api/v1/patterns/results/${symbol}`, {
        params: {interval, limit, startTime, endTime, scope, apiKeyId},
    });
    return res.data?.data ?? [];
}

/** 查询已持久化的区域形态结果（只读，不触发检测，供定时轮询） */
export async function getZoneResults(
    symbol: string,
    interval: string = '1H',
    startTime?: number,
    endTime?: number,
    apiKeyId?: number,
): Promise<ZoneDetectResult[]> {
    const res = await api.get(`/api/v1/patterns/results/zone/${symbol}`, {
        params: {interval, startTime, endTime, apiKeyId},
    });
    return res.data?.data ?? [];
}

/** 获取形态结果详情 */
export async function getPatternDetail(id: number, apiKeyId?: number): Promise<PatternDetectResult | null> {
    const res = await api.get(`/api/v1/patterns/result/${id}`, {
        params: {apiKeyId},
    });
    return res.data?.data ?? null;
}

/** 获取AI分析结果 */
export async function getAIAnalysis(patternId: number, apiKeyId?: number): Promise<AIAnalysis | null> {
    const res = await api.get(`/api/v1/patterns/analysis/${patternId}`, {
        params: {apiKeyId},
    });
    return res.data?.data ?? null;
}

/** 触发AI分析（LLM调用耗时较长，超时 120s） */
export async function triggerAIAnalysis(patternId: number): Promise<AIAnalysis | null> {
    const res = await api.post(`/api/v1/patterns/analyze/${patternId}`, null, {timeout: 120000});
    return res.data?.data ?? null;
}

/** 检测区域形态（支撑阻力区、盘整箱体） */
export async function detectZones(
    symbol: string,
    interval: string = '1H',
    provider: string = 'OKX',
): Promise<ZoneDetectResult[]> {
    const res = await api.get(`/api/v1/patterns/zone/${symbol}`, {
        params: {interval, provider},
    });
    return res.data?.data ?? [];
}

/** 获取区域形态AI分析结果 */
export async function getZoneAIAnalysis(zoneId: number, apiKeyId?: number): Promise<AIAnalysis | null> {
    const res = await api.get(`/api/v1/patterns/zone/analysis/${zoneId}`, {
        params: {apiKeyId},
    });
    return res.data?.data ?? null;
}

/** 触发区域形态AI分析（超时 120s） */
export async function triggerZoneAIAnalysis(zoneId: number): Promise<AIAnalysis | null> {
    const res = await api.post(`/api/v1/patterns/zone/analyze/${zoneId}`, null, {timeout: 120000});
    return res.data?.data ?? null;
}

/** 获取形态AI能力开关配置（快交面板/叠加展示等） */
export async function getPatternCapabilities(isPoc?: boolean): Promise<{
    overlayEnabled: boolean;
    quickTradeEnabled: boolean;
    priceDeviationThreshold: number;
    maxCandleAge: number;
} | null> {
    const res = await api.get('/api/v1/patterns/capabilities', {
        headers: isPoc ? {'X-POC-Access': 'true'} : undefined,
    });
    return res.data?.data ?? null;
}

/** 获取形态回测统计 */
export async function getBacktestStats(
    patternCode: string,
    symbol: string,
    interval: string = '1H',
    days: number = 90,
    apiKeyId?: number,
): Promise<BacktestStats | null> {
    const res = await api.get(`/api/v1/patterns/backtest/${patternCode}/${symbol}`, {
        params: {interval, days, apiKeyId},
    });
    return res.data?.data ?? null;
}
